import { IOrder, IfsAvailabilityCode, ImporterLocationId, MAKE } from 'next-common';
import { IVehicleStock, VehicleStockStatus, VehicleCondition } from 'sales-types-common';
import { IDynamicListStockTableData } from '../../components/common-components/DynamicListStock';
import { ValidationErrorCode, getErrorMessageForError } from '../../components/notifications';
import { IUnavailableVehicleWarning, checkVehicleSoldOrReserved } from './vehicleOrder';
import { isEmpty } from 'lodash';
import { IDynamicListStockData } from '@common';
import { isVehicleReserved } from '@utils/reservation/reservationUtils';

export const importerLocationIds = Object.values<string>(ImporterLocationId);

export const isVehicleSold = (vehicle: IVehicleStock): boolean => {
  return vehicle?.status === VehicleStockStatus.SOLD || vehicle?.availabilityCode?.includes(IfsAvailabilityCode.Sold);
};
export const isVehicleScrap = (vehicle: IVehicleStock): boolean => {
  return vehicle?.status === VehicleStockStatus.QA;
};

export const isDemoVehicle = (vehicle: IVehicleStock | IDynamicListStockTableData | IDynamicListStockData): boolean => {
  return vehicle?.status === VehicleStockStatus.DEMO || vehicle?.availabilityCode?.includes(IfsAvailabilityCode.Demo);
};

export const isFreeVehicle = (vehicle: IVehicleStock | IDynamicListStockTableData): boolean => {
  return vehicle?.status === VehicleStockStatus.FREE || vehicle?.availabilityCode?.includes(IfsAvailabilityCode.Free);
};

export const isVehicleSoftLocked = (vehicle: IVehicleStock, orderId: string): boolean => {
  return vehicle?.orderId && vehicle?.orderId !== orderId;
};

export const isVehicleProcessedByOtherDealer = (availabilityCode: string, employeeDealerIds: string[]): boolean => {
  const locationId = availabilityCode?.split('-')?.[0];
  return !employeeDealerIds.includes(locationId) && availabilityCode?.includes(IfsAvailabilityCode.Processed);
};

export const isVehicleProcessedByDealer = (availabilityCode: string, employeeDealerIds: string[]): boolean => {
  const locationId = availabilityCode?.split('-')?.[0];
  return employeeDealerIds.includes(locationId) && availabilityCode?.includes(IfsAvailabilityCode.Processed);
};

export const isVehicleFreeAtDealer = (availabilityCode: string, employeeDealerIds: string[]): boolean => {
  const locationId = availabilityCode?.split('-')?.[0];
  return (
    employeeDealerIds.includes(locationId) &&
    (availabilityCode?.includes(IfsAvailabilityCode.Free) || availabilityCode?.includes(IfsAvailabilityCode.Demo))
  );
};

export const isVehicleAvailableFromOtherDealer = (vehicle: IVehicleStock, employeeDealerIds: string[]): boolean => {
  const locationId = vehicle?.availabilityCode?.split('-')?.[0];
  return !employeeDealerIds?.includes(locationId) && (isFreeVehicle(vehicle) || isDemoVehicle(vehicle));
};

export const isVehicleOnImporterLocation = (
  locationId: string,
  vehicleCondition?: string,
  status?: string
): boolean => {
  const vehicleConditions = [VehicleCondition.USED, VehicleCondition.USEDIMPORT];
  if (vehicleConditions.includes(vehicleCondition as VehicleCondition) && status === VehicleStockStatus.FREE) {
    return false;
  }
  return importerLocationIds?.length && importerLocationIds?.includes(locationId?.toString());
};

export const isVehicleOnDealerLocation = (locationId: string = '', employeeDealerIds: string[] = []): boolean => {
  return employeeDealerIds?.length && employeeDealerIds?.includes(locationId?.toString());
};

export const isVehicleNotInStock = (vehicleCondition: string, status: string): boolean => {
  const vehicleConditions = [VehicleCondition.NEW, VehicleCondition.USED];
  return vehicleConditions.includes(vehicleCondition as VehicleCondition) && status === VehicleStockStatus.SOLD;
};

export const isVehicleOnOtherDealerLocation = (locationId: string = '', employeeDealerIds: string[] = []): boolean => {
  if (isVehicleOnImporterLocation(locationId)) return false;
  if (isVehicleOnDealerLocation(locationId, employeeDealerIds)) return false;
  return true;
};

export const isVehicleDisableAtCurrentLocation = (vehicle: IVehicleStock, validDealerIds: string[]): boolean => {
  if (isVehicleOnOtherDealerLocation(vehicle?.locationId, validDealerIds)) {
    if (isVehicleAvailableFromOtherDealer(vehicle, validDealerIds)) return false;
    return true;
  }
  if (isVehicleOnImporterLocation(vehicle?.locationId)) return false;
  if (isVehicleOnDealerLocation(vehicle?.locationId, validDealerIds)) {
    if (isVehicleProcessedByDealer(vehicle?.availabilityCode, validDealerIds)) return false;
    if (isVehicleFreeAtDealer(vehicle?.availabilityCode, validDealerIds)) return false;
    return true;
  }
  return true;
};

export const isVehicleAvailableAtImporterLocation = (vehicle: IVehicleStock): boolean => {
  const locationId = vehicle?.availabilityCode?.split('-')?.[0];
  return importerLocationIds?.includes(locationId) && (isFreeVehicle(vehicle) || isDemoVehicle(vehicle));
};

//This function is only used for perform validation when vehicle is on other dealer location.
export const isVehicleValidForReserveOrSignin = (
  vehicle: IVehicleStock,
  order: IOrder = null,
  employeeDealerIds: string[] = []
): boolean => {
  //If Vehicle is reserved or Sold from same order then its always valid vehicle
  const isSerialNoMatchedOrEmpty = isEmpty(order?.vehicles?.[0]?.serialNo)
    ? true
    : order?.vehicles?.[0]?.serialNo === order?.vehicleOrderStatus?.vehicleId;
  if (
    (!isEmpty(order?.vehicleOrderStatus?.status) && isSerialNoMatchedOrEmpty) ||
    order?.reserveDetails?.quotationNumber
  )
    return true;
  if (isVehicleNotInStock(vehicle?.vehicleCondition, vehicle?.status)) return true;
  if (isVehicleOnOtherDealerLocation(vehicle?.locationId, employeeDealerIds)) return false;
  if (isVehicleOnImporterLocation(vehicle?.locationId, vehicle?.vehicleCondition, vehicle?.status)) return true;

  if (isVehicleOnDealerLocation(vehicle?.locationId, employeeDealerIds)) {
    //Availability code must be LA_AL(FREE) OR TI_AL(Processed)
    if (isVehicleProcessedByDealer(vehicle?.availabilityCode, employeeDealerIds)) return true;
    if (isVehicleFreeAtDealer(vehicle?.availabilityCode, employeeDealerIds)) return true;
    if (isVehicleSold(vehicle) || isVehicleReserved(vehicle)) return true;
    return false;
  }

  return false;
};

export const isSelectedVehileIsUnavailable = (
  order: IOrder,
  currentVehicleStatus: IUnavailableVehicleWarning = null,
  vehicle: IVehicleStock = null
) => {
  currentVehicleStatus = currentVehicleStatus || checkVehicleSoldOrReserved(vehicle, order?.id);
  return currentVehicleStatus?.isTaken && !order?.reserveDetails?.quotationNumber;
};

export const getVehicleAvailabityStatus = (vehicle: IDynamicListStockTableData): string => {
  if (isDemoVehicle(vehicle)) return 'DE';
  if (vehicle?.status === VehicleStockStatus.FREE) {
    if (vehicle?.availabilityCode?.includes(IfsAvailabilityCode.Free)) return 'LA';
    if (vehicle?.availabilityCode?.includes(IfsAvailabilityCode.Processed)) return 'TI';
  }
  return '';
};

export const OtherDealerVehicleReserveOrSignValidationMsg = (availabilityCode: string = ''): string => {
  const baseMessage = getErrorMessageForError(ValidationErrorCode.OTHER_DEALER_VEHICLE_RESERVE_OR_SIGN_VALIDATION);
  const availabilitySuffix = availabilityCode ? `(${availabilityCode})` : '';
  return `${baseMessage} ${availabilitySuffix}`;
};

export const OtherDealerVehicleChoosenValidationMsg = (availabilityCode: string = ''): string => {
  return `${getErrorMessageForError(ValidationErrorCode.OTHER_DEALER_VEHICLE_RESERVE_OR_SIGN_VALIDATION)} Er du sikker på at du vil velge denne bilen?`;
};

export const importerVehicleValidation = (): string => {
  return `Denne bilen tilhører importør, og må tildeles din forhandler av importør før du kan signere. Er du sikker på at du vil velge denne bilen?`;
};

//will move this code to next-common in noraml release
const BRAND_IMPORTER_LOCATION_MAP: Partial<Record<MAKE, ImporterLocationId[]>> = {
  [MAKE.Citroen]: [ImporterLocationId.CITROEN_DS_NORWAY],
  [MAKE.DS]: [ImporterLocationId.CITROEN_DS_NORWAY],
  [MAKE.Kia]: [ImporterLocationId.KIA_NORWAY],
  [MAKE.Peugeot]: [ImporterLocationId.PEUGEOT_NORWAY],
  [MAKE.Opel]: [ImporterLocationId.OPEL_NORWAY],

  [MAKE.Mercedes]: [
    ImporterLocationId.MERCEDES_BENZ_NORWAY_TRANSPORTER,
    ImporterLocationId.MERCEDES_BENZ_NORWAY_COMMERCIAL,
    ImporterLocationId.MERCEDES_BENZ_NORWAY_PERSONAL,
    ImporterLocationId.MERCEDES_BENZ_NORWAY_BUS,
  ],

  [MAKE.Smart]: [ImporterLocationId.SMART_NORWAY_PERSONAL],
  [MAKE.Fiat]: [ImporterLocationId.FIAT_NORWAY],
  [MAKE.AlfaRomeo]: [ImporterLocationId.ALFA_NORWAY],
  [MAKE.Jeep]: [ImporterLocationId.JEEP_NORWAY],
};

export const importerNameMap: Partial<Record<ImporterLocationId, string>> = {
  [ImporterLocationId.CITROEN_DS_NORWAY]: 'Citroen Norge AS',
  [ImporterLocationId.KIA_NORWAY]: 'Kia Bil Norge',
  [ImporterLocationId.PEUGEOT_NORWAY]: 'Peugeot Norge',
  [ImporterLocationId.OPEL_NORWAY]: 'Opel Norge',
  [ImporterLocationId.MERCEDES_BENZ_NORWAY_TRANSPORTER]: 'MB Transporter',
  [ImporterLocationId.MERCEDES_BENZ_NORWAY_COMMERCIAL]: 'MB Lastebil',
  [ImporterLocationId.MERCEDES_BENZ_NORWAY_PERSONAL]: 'MB Personbil',
  [ImporterLocationId.MERCEDES_BENZ_NORWAY_BUS]: 'MB Buss',
  [ImporterLocationId.SMART_NORWAY_PERSONAL]: 'SMART',
  [ImporterLocationId.FIAT_NORWAY]: 'Fiat',
  [ImporterLocationId.ALFA_NORWAY]: 'Alfa Romeo',
  [ImporterLocationId.JEEP_NORWAY]: 'JEEP',
};

export const getImporterLocationIdsByBrand = (brand?: string) => {
  if (!brand) return [];
  return BRAND_IMPORTER_LOCATION_MAP[brand as MAKE] ?? [];
};
